import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { spreadsheetLinksApi } from "@/features/tasks/api/spreadsheetLinks";

const slKeys = {
  forTask: (taskId: string) => ["task-spreadsheet-links", taskId] as const,
  forSubTask: (subtaskId: string) => ["subtask-spreadsheet-links", subtaskId] as const,
};

export function useTaskSpreadsheetLinks(taskId: string) {
  return useQuery({
    queryKey: slKeys.forTask(taskId),
    queryFn: () => spreadsheetLinksApi.listForTask(taskId),
    enabled: !!taskId,
  });
}

export function useCreateTaskSpreadsheetLink(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; embed_code: string }) =>
      spreadsheetLinksApi.createForTask(taskId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forTask(taskId) }),
  });
}

export function useDeleteTaskSpreadsheetLink(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => spreadsheetLinksApi.deleteForTask(taskId, linkId),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forTask(taskId) }),
  });
}

export function useRenameTaskSpreadsheetLink(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, name }: { linkId: string; name: string }) =>
      spreadsheetLinksApi.renameForTask(taskId, linkId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forTask(taskId) }),
  });
}

export function useUpdateTaskSpreadsheetLink(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, data }: { linkId: string; data: { name: string; embed_code: string } }) =>
      spreadsheetLinksApi.updateForTask(taskId, linkId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forTask(taskId) }),
  });
}

export function useSubTaskSpreadsheetLinks(taskId: string, subtaskId: string) {
  return useQuery({
    queryKey: slKeys.forSubTask(subtaskId),
    queryFn: () => spreadsheetLinksApi.listForSubTask(taskId, subtaskId),
    enabled: !!taskId && !!subtaskId,
  });
}

export function useCreateSubTaskSpreadsheetLink(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; embed_code: string }) =>
      spreadsheetLinksApi.createForSubTask(taskId, subtaskId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forSubTask(subtaskId) }),
  });
}

export function useDeleteSubTaskSpreadsheetLink(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => spreadsheetLinksApi.deleteForSubTask(taskId, subtaskId, linkId),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forSubTask(subtaskId) }),
  });
}

export function useRenameSubTaskSpreadsheetLink(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, name }: { linkId: string; name: string }) =>
      spreadsheetLinksApi.renameForSubTask(taskId, subtaskId, linkId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forSubTask(subtaskId) }),
  });
}

export function useUpdateSubTaskSpreadsheetLink(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, data }: { linkId: string; data: { name: string; embed_code: string } }) =>
      spreadsheetLinksApi.updateForSubTask(taskId, subtaskId, linkId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: slKeys.forSubTask(subtaskId) }),
  });
}

export function useCreateTaskBackup(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => spreadsheetLinksApi.createTaskBackup(taskId, linkId),
    onSuccess: (_data, linkId) =>
      qc.invalidateQueries({ queryKey: ["spreadsheet-backups", `/tasks/${taskId}/spreadsheet-links/${linkId}/backups/`] }),
  });
}

export function useCreateSubTaskBackup(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => spreadsheetLinksApi.createSubTaskBackup(taskId, subtaskId, linkId),
    onSuccess: (_data, linkId) =>
      qc.invalidateQueries({ queryKey: ["spreadsheet-backups", `/tasks/${taskId}/subtasks/${subtaskId}/spreadsheet-links/${linkId}/backups/`] }),
  });
}
